import { useEffect, useMemo, useState } from "react";
import {
  Box,
  ButtonBase,
  CircularProgress,
  Fade,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { Block, Button, WebBackButton } from "../../../components/shared";
import { useNavigate, useLocation } from "react-router-dom";
import { useTelegram } from "../../../hooks";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { enqueueSnackbar } from "notistack";
import {
  createReferralPayout,
  getReferralOverview,
} from "../../../services/referrals";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

interface LocationState {
  balance?: string;
  min_payout?: string;
  networks?: string[];
}

function formatUsdt(v: string): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0.00";
  const locale = i18n.language === "en" ? "en-US" : "ru-RU";
  return n.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function WithdrawlPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tg } = useTelegram();
  const qc = useQueryClient();
  const { t } = useTranslation("referral");

  // Стейт из state роутера — быстрый снапшот. Если пользователь пришёл сюда
  // напрямую или обновил страницу — подтягиваем overview с бэка.
  const state = (location.state as LocationState) ?? {};
  const overviewQuery = useQuery(
    ["referral", "overview"],
    getReferralOverview,
    {
      staleTime: 30_000,
      enabled: !state.balance || !state.networks,
    },
  );
  const overview = overviewQuery.data;

  const balance = useMemo(
    () => state.balance ?? overview?.account.balance ?? "0",
    [state.balance, overview],
  );
  const minPayout = useMemo(
    () => state.min_payout ?? overview?.settings.min_payout_usdt ?? "10",
    [state.min_payout, overview],
  );
  const networks = useMemo(
    () => state.networks ?? overview?.settings.payout_networks ?? ["TRC20"],
    [state.networks, overview],
  );

  const [amount, setAmount] = useState("");
  const [network, setNetwork] = useState(networks[0] ?? "TRC20");
  const [address, setAddress] = useState("");

  useEffect(() => {
    setNetwork(networks[0] ?? "TRC20");
  }, [networks]);

  const mutation = useMutation(createReferralPayout, {
    onSuccess: () => {
      enqueueSnackbar(t("payout.success_pending"), {
        variant: "success",
      });
      qc.invalidateQueries(["referral"]);
      navigate("/referral");
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string | string[] } } })
          ?.response?.data?.message ?? t("payout.create_failed");
      enqueueSnackbar(Array.isArray(msg) ? msg.join(", ") : msg, {
        variant: "error",
      });
    },
  });

  useEffect(() => {
    tg.BackButton.show();
    const handler = () => navigate("/referral");
    tg.onEvent("backButtonClicked", handler);
    return () => {
      tg.BackButton.hide();
      tg.offEvent("backButtonClicked", handler);
    };
  }, [navigate, tg]);

  const numeric = Number(amount);
  const balanceNum = Number(balance);
  const minPayoutNum = Number(minPayout);
  const amountOk =
    Number.isFinite(numeric) &&
    numeric > 0 &&
    numeric >= minPayoutNum &&
    numeric <= balanceNum;
  const addressOk = address.trim().length >= 4;
  const canSubmit = amountOk && addressOk && !mutation.isLoading;

  const handleMax = () => {
    setAmount(balance);
  };

  const handleSubmit = () => {
    mutation.mutate({
      amount,
      networkCode: network,
      address: address.trim(),
    });
  };

  return (
    <Fade in>
      <Box
        sx={{
          marginBottom: "100px",
          width: "100%",
          maxWidth: "460px",
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        <WebBackButton to="/referral" title={t("payout.title")} />

        <Block>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography sx={{ fontSize: "13px", opacity: 0.55 }}>
                {t("payout.available")}
              </Typography>
              <Typography
                sx={{ fontSize: "22px", fontWeight: 700, marginTop: "2px" }}
              >
                {formatUsdt(balance)} USDT
              </Typography>
            </Box>
            <ButtonBase
              onClick={handleMax}
              sx={{
                padding: "6px 14px",
                borderRadius: "999px",
                fontSize: "13px",
                fontWeight: 600,
                backgroundColor: (t) =>
                  t.palette.mode === "dark"
                    ? "rgba(255,255,255,0.06)"
                    : "rgba(0,0,0,0.05)",
                "&:active": { transform: "scale(0.97)" },
              }}
            >
              {t("payout.all")}
            </ButtonBase>
          </Box>
        </Block>

        <Block sx={{ marginTop: "12px" }}>
          <Typography sx={{ fontSize: "13px", opacity: 0.55 }}>
            {t("payout.amount_label")}
          </Typography>
          <TextField
            value={amount}
            onChange={(e) => {
              const v = e.target.value.replace(",", ".");
              if (/^\d*\.?\d{0,10}$/.test(v)) setAmount(v);
            }}
            placeholder="0.00"
            fullWidth
            inputMode="decimal"
            sx={{
              "& .MuiOutlinedInput-root": {
                padding: "0",
                "& fieldset": { border: "none" },
              },
              "& input": {
                fontSize: "24px",
                fontWeight: 600,
                padding: "8px 0",
              },
            }}
          />
          <Typography
            sx={{
              fontSize: "12px",
              opacity: 0.55,
              marginTop: "4px",
            }}
          >
            {t("payout.minimum", { amount: formatUsdt(minPayout) })}
          </Typography>
        </Block>

        <Block sx={{ marginTop: "12px" }}>
          <Typography sx={{ fontSize: "13px", opacity: 0.55 }}>
            {t("payout.network")}
          </Typography>
          <Select
            value={network}
            onChange={(e) => setNetwork(e.target.value)}
            fullWidth
            variant="standard"
            disableUnderline
            sx={{
              fontSize: "16px",
              fontWeight: 500,
              marginTop: "4px",
              "& .MuiSelect-select": { paddingY: "6px" },
            }}
          >
            {networks.map((n) => (
              <MenuItem key={n} value={n}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Icon icon="solar:hierarchy-2-linear" width={16} height={16} />
                  USDT · {n}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </Block>

        <Block sx={{ marginTop: "12px" }}>
          <Typography sx={{ fontSize: "13px", opacity: 0.55 }}>
            {t("payout.wallet_address")}
          </Typography>
          <TextField
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder={t("payout.address_placeholder")}
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                padding: "0",
                "& fieldset": { border: "none" },
              },
              "& input": {
                fontSize: "15px",
                padding: "10px 0",
                fontFamily: "monospace",
              },
            }}
          />
        </Block>

        <Typography
          sx={{
            marginTop: "16px",
            fontSize: "12px",
            opacity: 0.55,
            paddingX: "6px",
          }}
        >
          {t("payout.moderation_note")}
        </Typography>

        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          sx={{ marginTop: "16px" }}
        >
          {mutation.isLoading ? (
            <CircularProgress size={22} sx={{ color: "#fff" }} />
          ) : (
            t("payout.withdraw_amount", {
              amount: amount ? formatUsdt(amount) : "0",
            })
          )}
        </Button>
      </Box>
    </Fade>
  );
}
